import type { Table } from "@tanstack/react-table";

import type { DataTableServerPagination } from "./types";

/**
 * Pie de la tabla: cuántas filas se ven y cómo pasar de página.
 *
 * Dos modos, porque el portal tiene los dos casos:
 *
 *   - **Cliente**: todo está en memoria, así que se sabe cuántas páginas hay.
 *   - **Servidor**: la API pagina por cursor y **no cuenta** el total, así
 *     que aquí no se puede decir «página 3 de 12» sin inventárselo. Solo
 *     anterior y siguiente, que es lo que el backend sabe responder.
 */
export function DataTablePagination<TData>({
  table,
  server,
  visibles,
}: {
  table: Table<TData>;
  server?: DataTableServerPagination;
  visibles: number;
}) {
  if (server) {
    return (
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-slate-500">
          {visibles} {visibles === 1 ? "resultado" : "resultados"} en esta página
        </span>
        <div className="flex gap-2">
          <Boton onClick={server.onPrevious} disabled={!server.hasPrevious}>
            Anterior
          </Boton>
          <Boton onClick={server.onNext} disabled={!server.hasNext}>
            Siguiente
          </Boton>
        </div>
      </div>
    );
  }

  const estado = table.getState().pagination;
  const total = table.getFilteredRowModel().rows.length;
  const paginas = table.getPageCount();
  const desde = total === 0 ? 0 : estado.pageIndex * estado.pageSize + 1;
  const hasta = Math.min((estado.pageIndex + 1) * estado.pageSize, total);

  if (paginas <= 1) {
    return (
      <p className="mt-3 text-xs text-slate-500">
        {total} {total === 1 ? "resultado" : "resultados"}
      </p>
    );
  }

  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
      <span className="text-xs text-slate-500">
        {desde}–{hasta} de {total}
      </span>
      <div className="flex items-center gap-2">
        <Boton onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
          Anterior
        </Boton>
        <span className="text-xs text-slate-500">
          Página {estado.pageIndex + 1} de {paginas}
        </span>
        <Boton onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
          Siguiente
        </Boton>
      </div>
    </div>
  );
}

function Boton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded-lg border border-white/15 bg-white/[0.04] px-3 py-1.5 text-sm text-slate-300 transition hover:bg-white/[0.09] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
